import {
  getGetWorkspaceMemberApiV1QueryKey,
  getGetWorkspaceMembersApiV1QueryKey,
  useDeleteWorkspaceMemberApiV1,
  useGetWorkspaceMemberApiV1Suspense,
  useUpdateWorkspaceMemberApiV1,
} from "@/api/__generated__/api.schema";
import { WorkspaceMemberRole } from "@/api/__generated__/models";
import { Profile } from "@/components/profile";
import { Allowed } from "@/components/role/allowed";
import { useUser } from "@/hooks/auth/use-user";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { cn } from "@/lib/utils";
import { useToast } from "@backpac/monica";
import { Button, DropdownTriggerButton, Separator, Typography, useConfirm } from "@backpac/monica";
import { useQueryClient } from "@tanstack/react-query";
import { isEqual } from "es-toolkit";
import { Suspense, useState } from "react";
import { WorkspaceManagedChannelSelect } from "./workspace-managed-channel-select";

interface WorkspaceMemberManageFormProps {
  memberId: string;
  onClose: () => void;
}

export function WorkspaceMemberManageForm({ memberId, onClose }: WorkspaceMemberManageFormProps) {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();
  const {
    data: {
      data: { item: member },
    },
  } = useGetWorkspaceMemberApiV1Suspense(workspaceId, memberId);
  const { data: currentUser } = useUser();

  const { mutate: updateWorkspaceMember, isPending } = useUpdateWorkspaceMemberApiV1({
    mutation: {
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getGetWorkspaceMemberApiV1QueryKey(workspaceId, memberId) }),
          queryClient.invalidateQueries({ queryKey: getGetWorkspaceMembersApiV1QueryKey(workspaceId) }),
        ]);
        onClose();
      },
    },
  });

  const [role, setRole] = useState<WorkspaceMemberRole>(member.role);
  const [permittedChannels, setPermittedChannels] = useState<string[]>(
    member.permittedChannels.map((channel) => channel.id),
  );

  const isDirty =
    role !== member.role ||
    !isEqual(
      permittedChannels,
      member.permittedChannels.map((channel) => channel.id),
    );

  const isValid = role !== WorkspaceMemberRole.MEMBER || permittedChannels.length > 0;

  return (
    <div className="space-y-6">
      <Profile name={member.name} description={member.email} profileImageUrl={member.thumbnailUrl} />
      <Separator />
      <div className="space-y-3">
        <FormLabel>Role</FormLabel>
        <RoleSelector value={role} onChange={setRole} />
      </div>
      <div className="space-y-2 mt-4">
        <FormLabel>Managed channels</FormLabel>
        <Suspense
          fallback={
            <DropdownTriggerButton size="md" className="w-full font-normal text-xs">
              Search channels
            </DropdownTriggerButton>
          }
        >
          {role === WorkspaceMemberRole.MEMBER ? (
            <WorkspaceManagedChannelSelect defaultValues={permittedChannels} onChange={setPermittedChannels} />
          ) : (
            <DropdownTriggerButton size="md" disabled={true} className="w-full font-normal text-xs bg-backgroundLight">
              All
            </DropdownTriggerButton>
          )}
        </Suspense>
      </div>
      <Separator />
      <div className="flex gap-3">
        <Button
          variant="black"
          disabled={!isDirty || !isValid}
          loading={isPending}
          onClick={() =>
            updateWorkspaceMember({
              workspaceId,
              userId: memberId,
              data: { role, permittedChannels: role === WorkspaceMemberRole.MEMBER ? permittedChannels : undefined },
            })
          }
        >
          Save
        </Button>
        {currentUser?.id !== memberId ? (
          <RemoveMemberButton
            className="fixed bottom-6"
            memberId={member.userId}
            memberName={member.name}
            memberEmail={member.email}
          />
        ) : null}
      </div>
    </div>
  );
}

interface LabelProps {
  children: React.ReactNode;
}

function FormLabel({ children }: LabelProps) {
  return (
    <Typography size="xs" weight={400}>
      {children}
    </Typography>
  );
}

interface RoleSelectorProps {
  value: WorkspaceMemberRole;
  onChange: (role: WorkspaceMemberRole) => void;
}

function RoleSelector({ value, onChange }: RoleSelectorProps) {
  const { confirm } = useConfirm();

  return (
    <div className="flex gap-2">
      <Allowed role={WorkspaceMemberRole.OWNER}>
        <Button
          type="button"
          size="md"
          variant="outlined"
          className={cn({
            "bg-borderLight": value === WorkspaceMemberRole.OWNER,
          })}
          onClick={async () => {
            const confirmed = await confirm({
              title: "Transfer ownership",
              description:
                "There can only be one owner in the workspace. Transferring ownership to another member will change your role to admin. Are you sure you want to transfer ownership?",
              confirmText: "Transfer ownership",
              cancelText: "Cancel",
            });
            if (confirmed) {
              onChange(WorkspaceMemberRole.OWNER);
            }
          }}
        >
          Owner
        </Button>
      </Allowed>
      <Button
        type="button"
        size="md"
        variant="outlined"
        className={cn({
          "bg-borderLight": value === WorkspaceMemberRole.ADMIN,
        })}
        onClick={() => onChange(WorkspaceMemberRole.ADMIN)}
      >
        Admin
      </Button>
      <Button
        type="button"
        size="md"
        variant="outlined"
        className={cn({
          "bg-borderLight": value === WorkspaceMemberRole.MEMBER,
        })}
        onClick={() => onChange(WorkspaceMemberRole.MEMBER)}
      >
        Member
      </Button>
    </div>
  );
}

function RemoveMemberButton({
  className,
  memberId,
  memberName,
  memberEmail,
}: {
  className?: string;
  memberId: string;
  memberName: string;
  memberEmail: string;
}) {
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();
  const { mutate: removeMember, isPending } = useDeleteWorkspaceMemberApiV1({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetWorkspaceMembersApiV1QueryKey(workspaceId) });
        toast({ title: "Member removed" });
      },
    },
  });

  const handleRemove = async () => {
    const confirmed = await confirm({
      addon: <i className="inline-block i-bp-warning size-4 text-colorRedDark" />,
      title: "Confirm member removal",
      description: `Are you sure you want to remove ${memberName} (${memberEmail}) from the workspace?`,
      confirmText: "Remove",
      confirmVariant: "error",
      cancelText: "Cancel",
    });

    if (confirmed) {
      removeMember({ workspaceId, userId: memberId });
    }
  };

  return (
    <Button variant="error" className={cn(className)} onClick={handleRemove} loading={isPending}>
      Remove from workspace
    </Button>
  );
}
